import React, { useState, useEffect, useCallback, useRef } from "react";
import "./../../../../assets/css/purchase/form.css";

import InitialDataFormLayout from "../components/InitialDataFormLayout";
import {
  labelStyle,
  inputStyle,
  selectStyle,
  sectionHeaderStyle,
  sectionIconStyle,
  sectionTitleStyle,
} from "../components/formStyles";

import { deleteData, getAlert, insertData, updateData } from "../../../../utils/SamuiUtils";

const DEFAULT_FORM = {
  TimeUnit_Id: 0,
  TimeUnit_Code: "",
  TimeUnit_Name: "",
  TimeUnit_Type: "CONTRACT",
  TimeUnit_Moment: "months",
  TimeUnit_Sort: "",
  TimeUnit_Status: "Y",
};

const TimeUnitForm = ({
  callInitialize,
  mode,
  name,
  formData,
  setFormData,
  selectedData,
}) => {
  const [initialFormData, setInitialFormData] = useState(DEFAULT_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (mode === "I") {
      setFormData(DEFAULT_FORM);
      setInitialFormData(DEFAULT_FORM);
    } else if (mode === "U" && selectedData) {
      const editData = {
        TimeUnit_Id: selectedData.TimeUnit_Id,
        TimeUnit_Code: selectedData.TimeUnit_Code ?? "",
        TimeUnit_Name: selectedData.TimeUnit_Name || "",
        TimeUnit_Type: selectedData.TimeUnit_Type || "CONTRACT",
        TimeUnit_Moment: selectedData.TimeUnit_Moment || "",
        TimeUnit_Sort: selectedData.TimeUnit_Sort ?? "",
        TimeUnit_Status: selectedData.TimeUnit_Status || "Y",
      };
      setFormData(editData);
      setInitialFormData(editData);
    }
  }, [mode, selectedData, setFormData]);

  const handleChange = (event) => {
    const { name: fieldName, value } = event.target;
    setFormData((prev) => {
      const next = { ...prev, [fieldName]: value };
      if (fieldName === "TimeUnit_Type" && value === "SERVICE") {
        next.TimeUnit_Moment = "";
      }
      return next;
    });
  };

  const handleInsert = useCallback(async () => {
    try {
      const { TimeUnit_Id, ...dataToInsert } = formData;
      const fields = Object.keys(dataToInsert).join(", ");
      const values = Object.values(dataToInsert);

      await insertData("Tb_Set_TimeUnit", fields, values);
      await callInitialize();
      setFormData(DEFAULT_FORM);
      setInitialFormData(DEFAULT_FORM);
    } catch (error) {
      console.error("Error in handleInsert:", error);
      getAlert("FAILED", "ไม่สามารถบันทึกข้อมูลได้");
    }
  }, [formData, callInitialize, setFormData]);

  const handleUpdate = useCallback(async () => {
    try {
      const where = { TimeUnit_Id: formData.TimeUnit_Id };
      const { TimeUnit_Id, ...values } = formData;
      const res = await updateData("Tb_Set_TimeUnit", values, where);
      if (res) {
        await callInitialize();
      }
    } catch (error) {
      getAlert("FAILED", "ไม่สามารถอัปเดตข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว");
    }
  }, [formData, callInitialize]);

  const handleDelete = async () => {
    try {
      getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", async (isConfirm) => {
        if (isConfirm && selectedData?.TimeUnit_Id !== undefined) {
          await deleteData(
            "Tb_Set_TimeUnit",
            `WHERE TimeUnit_Id = ${selectedData.TimeUnit_Id}`
          );
          await callInitialize();
        }
      });
    } catch (error) {
      getAlert("FAILED", "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง");
    }
  };

  const handleSubmit = useCallback(async () => {
    if (isSavingRef.current) return;
    if (!formData.TimeUnit_Code || !formData.TimeUnit_Name || !formData.TimeUnit_Type) {
      getAlert("WARNING", "โปรดกรอกข้อมูลให้ครบ");
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      if (mode === "I") {
        await handleInsert();
      } else if (mode === "U") {
        await handleUpdate();
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [formData, mode, handleInsert, handleUpdate]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        getAlert("C_DIALOG", "ต้องการยกเลิกการทำงานหรือไม่?", (isConfirm) => {
          if (isConfirm) {
            window.location.href = "/timeunit";
          }
        });
      }

      if (event.key === "F10") {
        event.preventDefault();
        if (!isSavingRef.current) handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSubmit]);

  const handleReset = () => {
    setFormData(initialFormData);
  };

  const isServiceType = formData.TimeUnit_Type === "SERVICE";

  return (
    <InitialDataFormLayout
      name={name}
      entityPath="/timeunit"
      mode={mode}
      onSubmit={handleSubmit}
      isSubmittingDisabled={isSaving}
      onBack={() => (window.location.href = "/timeunit")}
      onReset={handleReset}
      onDelete={mode === "U" ? handleDelete : undefined}
      callInitialize={callInitialize}
    >
      <div style={sectionHeaderStyle}>
        <i className="fas fa-clock" style={sectionIconStyle}></i>
        <h5 style={sectionTitleStyle}>หน่วยช่วงเวลา</h5>
      </div>
      <div className="row g-4">
        <div className="col-md-3">
          <label style={labelStyle}>รหัส</label>
          <input
            type="number"
            className="form-control"
            name="TimeUnit_Code"
            value={formData.TimeUnit_Code ?? ""}
            onChange={handleChange}
            style={inputStyle}
            min="1"
          />
        </div>
        <div className="col-md-5">
          <label style={labelStyle}>ชื่อหน่วย</label>
          <input
            type="text"
            className="form-control"
            name="TimeUnit_Name"
            value={formData.TimeUnit_Name || ""}
            onChange={handleChange}
            style={inputStyle}
            placeholder="เช่น เดือน / ครั้ง"
          />
        </div>
        <div className="col-md-4">
          <label style={labelStyle}>ประเภท</label>
          <select
            className="form-select"
            name="TimeUnit_Type"
            value={formData.TimeUnit_Type || "CONTRACT"}
            onChange={handleChange}
            style={selectStyle}
          >
            <option value="CONTRACT">ระยะสัญญา</option>
            <option value="SERVICE">จำนวนบริการตรวจเช็ค</option>
          </select>
        </div>
        <div className="col-md-4">
          <label style={labelStyle}>ใช้คำนวณระยะเวลา</label>
          <select
            className="form-select"
            name="TimeUnit_Moment"
            value={formData.TimeUnit_Moment || ""}
            onChange={handleChange}
            style={selectStyle}
            disabled={isServiceType}
          >
            <option value="">ไม่ใช้คำนวณ</option>
            <option value="days">วัน</option>
            <option value="weeks">สัปดาห์</option>
            <option value="months">เดือน</option>
            <option value="years">ปี</option>
          </select>
        </div>
        <div className="col-md-4">
          <label style={labelStyle}>ลำดับ</label>
          <input
            type="number"
            className="form-control"
            name="TimeUnit_Sort"
            value={formData.TimeUnit_Sort ?? ""}
            onChange={handleChange}
            style={inputStyle}
            min="0"
          />
        </div>
        <div className="col-md-4">
          <label style={labelStyle}>สถานะ</label>
          <select
            className="form-select"
            name="TimeUnit_Status"
            value={formData.TimeUnit_Status || "Y"}
            onChange={handleChange}
            style={selectStyle}
          >
            <option value="Y">ใช้งาน</option>
            <option value="N">ไม่ใช้งาน</option>
          </select>
        </div>
      </div>
    </InitialDataFormLayout>
  );
};

export default TimeUnitForm;
